import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from '@/app/store/hooks';
import { fetchDealerById } from '../services/dealers';
import { dealerKeys } from './useDealersQuery';
import type { Dealer } from '../types/dealers.types';

export interface HomeDealerRecordState {
    dealer: Dealer | null;
    /** True while an enabled request has not yet produced a row or an error. */
    isLoading: boolean;
}

/** The cache key is shared with useDealerQuery, so the return is gated on `enabled`:
 *  disabled always means no row and not loading. */
export const useHomeDealerRecordState = (options: { enabled: boolean }): HomeDealerRecordState => {
    const homeDealerId = useAppSelector((s) => s.auth.dealerId);
    const query = useQuery({
        queryKey: dealerKeys.detail(homeDealerId ?? ''),
        queryFn: () => fetchDealerById(homeDealerId!, { suppressForbiddenToast: true }),
        enabled: options.enabled && !!homeDealerId,
        staleTime: 5 * 60 * 1000,
    });
    if (!options.enabled) return { dealer: null, isLoading: false };
    return { dealer: query.data ?? null, isLoading: query.isLoading };
};

/** The user's full home dealer row: null until resolved and on any failure, so the
 *  caller's fallback simply does not engage. The dealer-first order sends no request. */
export const useHomeDealerRecord = (options: { enabled: boolean }): Dealer | null =>
    useHomeDealerRecordState(options).dealer;
